import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Video, X } from 'lucide-react'
import ProductAppVideoBoundary from '../common/ProductAppVideoBoundary'
import ProductMuxIframe from '../common/ProductMuxIframe'
import { DASHBOARD_MUX_THUMBNAIL_TIME_SEC } from '../../constants/dashboardMuxVideo'

const MUX_IFRAME_SRC = `https://player.mux.com/zTsugBZCjND2pwITlPGksaVwfIJObR8su01EkXTmoLSg?metadata-video-title=Company+Analysis+Page&video-title=Company+Analysis+Page&thumbnail-time=${DASHBOARD_MUX_THUMBNAIL_TIME_SEC}`

const HINT_STORAGE_KEY = 'rithmo_company_analysis_overview_video_hint_dismissed'

function readHintDismissed() {
  try {
    return localStorage.getItem(HINT_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function persistHintDismissed() {
  try {
    localStorage.setItem(HINT_STORAGE_KEY, '1')
  } catch {
    /* ignore */
  }
}

/**
 * Lazy-loaded Mux overview: iframe mounts only after the user opens the lightbox.
 * Wrapped in ProductAppVideoBoundary so this widget cannot appear on corporate routes.
 */
export default function CompanyAnalysisOverviewVideo() {
  return (
    <ProductAppVideoBoundary>
      <CompanyAnalysisOverviewVideoInner />
    </ProductAppVideoBoundary>
  )
}

function CompanyAnalysisOverviewVideoInner() {
  const [open, setOpen] = useState(false)
  const [hintDismissed, setHintDismissed] = useState(readHintDismissed)
  const wrapperRef = useRef(null)
  const hintRef = useRef(null)

  const dismissHint = () => {
    persistHintDismissed()
    setHintDismissed(true)
  }

  const openVideo = () => {
    persistHintDismissed()
    setHintDismissed(true)
    setOpen(true)
  }

  const close = () => setOpen(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const showHint = !hintDismissed

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <AnimatePresence>
        {showHint && (
          <motion.div
            ref={hintRef}
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.96 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="pointer-events-auto absolute top-full left-0 z-10 mt-2 w-[min(100vw-2rem,16rem)] max-w-[calc(100vw-2rem)] sm:w-56"
            role="status"
            aria-live="polite"
          >
            <motion.div
              className="relative"
              animate={{ y: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
            >
              <div className="relative rounded-xl border border-primary-200 bg-white px-3 py-2.5 pr-8 shadow-md shadow-primary-900/10">
                <button
                  type="button"
                  onClick={dismissHint}
                  className="absolute right-1.5 top-1.5 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Dismiss hint"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
                <p className="text-left text-sm font-medium leading-snug text-gray-800">
                  Watch the explainer video here!
                </p>
                <div
                  className="pointer-events-none absolute left-5 bottom-full mb-px flex -translate-x-1/2 flex-col items-center"
                  aria-hidden
                >
                  <div className="-mb-[8px] h-0 w-0 border-x-[8px] border-x-transparent border-b-[8px] border-b-white" />
                  <div className="h-0 w-0 border-x-[9px] border-x-transparent border-b-[9px] border-b-primary-200" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={openVideo}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary-200/90 bg-primary-50 text-primary-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700"
        aria-label="Open Company Analysis page overview video"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Video className="h-5 w-5" aria-hidden />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-6"
            role="presentation"
            onClick={close}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="company-analysis-overview-video-title"
              initial={{ opacity: 0, scale: 0.97, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 8 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="relative w-full max-w-[calc(100vw-1rem)] sm:max-w-[min(92vw,960px)] rounded-2xl bg-white shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                <h2 id="company-analysis-overview-video-title" className="text-base font-semibold text-gray-900">
                  Company Analysis page
                </h2>
                <button
                  type="button"
                  onClick={close}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                  aria-label="Close video"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="bg-black">
                <ProductMuxIframe
                  src={MUX_IFRAME_SRC}
                  className="block aspect-video w-full border-0"
                  title="Company Analysis page"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
